import React, { CSSProperties, FC, ReactNode, useCallback, useState } from 'react'

import { CustomFieldRestriction } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { FieldHeader } from '@avantstay/backoffice-core'

import * as S from '../../UploadArea.styles'
import { DragAreaStatusView, FilePreviewType, UploadAreaType } from '../../UploadArea.types'

interface FileDropAreaProps {
  areaId: string
  filePreviewType: FilePreviewType
  title?: string
  fieldRestriction?: CustomFieldRestriction
  dropAreaMessage?: ReactNode
  uploadAreaType?: UploadAreaType
  accept?: string
  areaStyles?: CSSProperties
  isDisabled?: boolean
  isDropAreaDisabled?: boolean
  /** Whether the drop zone is shown at all (e.g. hidden once a single-file area holds its file). */
  shouldRenderDropArea?: boolean
  /** Whether the file dialog is limited to one file. Independent of the drop zone's visibility. */
  uploadSingleFile?: boolean
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onDropFile: (e: React.DragEvent<HTMLLabelElement>) => void
}

export const FileDropArea: FC<FileDropAreaProps> = ({
  areaId,
  accept,
  title,
  fieldRestriction,
  uploadAreaType,
  filePreviewType,
  dropAreaMessage,
  areaStyles,
  isDisabled,
  isDropAreaDisabled,
  shouldRenderDropArea: shouldRenderDropAreaProp,
  uploadSingleFile,
  onInputChange,
  onDropFile,
}) => {
  const [status, setStatus] = useState<DragAreaStatusView>(DragAreaStatusView.idle)

  const isDocument = uploadAreaType === UploadAreaType.document
  const isReferencePhotoView = filePreviewType === FilePreviewType.referencePhoto
  const shouldRenderDropArea = isDocument || shouldRenderDropAreaProp

  const handleSetStatus = useCallback(
    (newStatus: DragAreaStatusView) => (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault()
      setStatus(newStatus)
    },
    [setStatus],
  )

  return shouldRenderDropArea || isReferencePhotoView ? (
    <FieldHeader.Default title={title || ''} restriction={title ? fieldRestriction : undefined}>
      <S.DropAreaWrapper $disabledUpload={isDisabled}>
        <S.HiddenInput
          id={areaId}
          accept={accept}
          multiple={!uploadSingleFile}
          type="file"
          onChange={onInputChange}
        />
        <S.DropArea
          htmlFor={areaId}
          active={status === DragAreaStatusView.dragOver}
          onDragOver={handleSetStatus(DragAreaStatusView.dragOver)}
          onDragLeave={handleSetStatus(DragAreaStatusView.idle)}
          onDrop={onDropFile}
          style={areaStyles}
          disabled={isDropAreaDisabled}
        >
          {dropAreaMessage ?? (
            <p>
              Drag here or <span>browse</span> to upload
            </p>
          )}
        </S.DropArea>
      </S.DropAreaWrapper>
    </FieldHeader.Default>
  ) : null
}
